<template>
  <q-inner-loading v-if="loading" showing color="secondary" />
  <property-report-settings
    v-else-if="report"
    :report="report"
    @submit="submit"
    @delete="deleteSubscription"
    @cancel="safelyNavigate"
  />
</template>

<script lang="ts">
import type { RentSourceSubscriptionAttributes } from 'common'
import { usePropertyReportStore } from '@/stores/propertyreport'
import PropertyReportSettings from '@/components/PropertyReports/PropertyReportSettings.vue'
import { useUserStore } from '@/stores/user'
import type { RouteLocation } from 'vue-router'
import { isLoaded } from '@/utils/loading'

export default {
  name: 'PropertyReportEditView',
  components: {
    PropertyReportSettings
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      this.showUnsavedChangesDialog().then((shouldLeave) => {
        if (shouldLeave) {
          next()
        } else {
          next(false)
        }
      })
    } else {
      next()
    }
  },
  beforeRouteUpdate(to, from, next) {
    if (this.hasUnsavedChanges) {
      this.showUnsavedChangesDialog().then((shouldLeave) => {
        if (shouldLeave) {
          next()
        } else {
          next(false)
        }
      })
    } else {
      next()
    }
  },
  props: {
    reportId: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      loading: false,
      report: null as RentSourceSubscriptionAttributes | null,
      hasUnsavedChanges: true
    }
  },
  computed: {
    organizationMembers() {
      return useUserStore().organizationMembers
    }
  },
  watch: {
    organizationMembers() {
      this.checkAuthorization()
    }
  },
  mounted() {
    this.loading = true
    usePropertyReportStore()
      .getReport(parseInt(this.reportId))
      .then((response) => {
        if (isLoaded(response)) {
          this.report = response.data
          this.checkAuthorization()
        } else {
          this.$router.push({ name: 'Dashboard' })
        }
      })
      .finally(() => {
        this.loading = false
      })
  },
  methods: {
    checkAuthorization() {
      const ownerIds = this.organizationMembers.map((_) => _.id)
      const ownerId = this.report?.user_id
      if (ownerIds.length && ownerId && !ownerIds.includes(ownerId)) {
        this.$router.push({ name: 'Dashboard' })
      }
    },
    submit(report: RentSourceSubscriptionAttributes) {
      if (!report) {
        console.warn('No report to update')
        return
      }
      usePropertyReportStore()
        .update(report)
        .then((res) => {
          this.hasUnsavedChanges = false
          if (res) {
            this.$tracker.updatedReport(res.id, 'property')
            this.$router.push({ name: 'Property Report', params: { reportId: res.id } })
          } else {
            this.$q.notify({
              message: 'Failed to update report',
              color: 'negative',
              position: 'top'
            })
            console.error('Failed to update report', res)
          }
        })
    },
    deleteSubscription() {
      this.hasUnsavedChanges = false
      usePropertyReportStore()
        .delete(parseInt(this.reportId))
        .then(() => {
          this.$tracker.deletedReport(parseInt(this.reportId), 'property')
          this.$router.push({ name: 'Dashboard' })
        })
    },
    safelyNavigate(to: RouteLocation) {
      this.hasUnsavedChanges = false
      this.$router.push(to)
    },
    showUnsavedChangesDialog() {
      return new Promise((resolve) => {
        this.$q
          .dialog({
            title: 'Leaving Report Editing',
            message:
              'You are about to leave this page. Any changes you have made will be lost. Are you sure you want to leave?',
            cancel: {
              label: 'Stay',
              flat: true,
              noCaps: true
            },
            ok: {
              label: 'Leave Page',
              flat: true,
              noCaps: true
            },
            persistent: true
          })
          .onOk(() => {
            resolve(true)
          })
          .onCancel(() => {
            resolve(false)
          })
      })
    }
  }
}
</script>
